<template>
    <tr class="odd:bg-gray-50 even:bg-white cursor-pointer hover:bg-[#EEF2FF]" :style="rowStyle" ref="crmDetailsBoundary" >
        <td
            class="px-2 py-1 border-r border-b border-gray-200 max-w-[280px] align-top text-left bg-inherit" 
            v-for="(field, index) in orderedCompanyFields" 
            :key="index"
            :style="getCellStyleByIndex(index)"
        >
            <div v-if="index==0" class="flex w-full justify-between items-start">
                <input type="checkbox" id="checkbox" class="accent-dark-secondary cursor-pointer" :checked="isSelected" @change="toggleSelected"/>
                <div class=""  @click="toggleViewCompanyEvents">
                <!-- <div v-if="companyEvents.length > 0" class=""  @click="toggleViewCompanyEvents"> -->
                    <div v-if="visibleCompanyEvents">
                        <ChevronUpMiniSVG />
                    </div>
                    <div v-else>
                        <ChevronDownMiniSVG />
                    </div>
                </div>
                <!-- <div class="text-gray-400" v-else>
                    -
                </div> -->
            </div>
            <div v-else-if="index==2" class="flex justify-between items-center space-x-2 truncate "  >
                <div class="" 
                    @click.prevent="openCRMDetails" 
                    @mouseover="showToolTipPopover('crmDetails', 'Open CRM Details')"
                    @mouseleave="hideToolTipPopover"
                >
                    <QueueListSVG class="w-5 h-5 p-0.5 rounded-sm hover:bg-indigo-200 hover:border hover:border-indigo-300"
                    />
                </div>
                <div class="w-full flex justify-start truncate hover:underline" @click="openCompanyModal">{{ handleFieldDisplay(field) }}</div>
            </div>
            <div v-else-if="index==3" class="flex justify-between items-center space-x-2 truncate">
                <div class="truncate">{{ handleFieldDisplay(field) }}</div>
                <div v-if="changedReportedEvent" class="w-[10px] flex h-full items-center justify-end">
                    <div class="w-2 h-2 bg-yellow-600 rounded-full "></div>
                </div>
            </div>
            <div v-else-if="condensedView && index==4" class="flex justify-between items-center truncate">
                <div v-if="showAddToProgressUpdate" class="w-4 h-4 flex justify-center items-center rounded-full cursor-pointer " @click.prevent="addCompanyToProgressUpdate">
                    <InsertSVG />
                </div>
                <div v-else class="w-full h-full cursor-default"></div>
            </div>
            <div v-else class="truncate flex justify-start items-start">
                {{ handleFieldDisplay(field) }}
            </div>
        </td>
        <ToolTip
                v-if="visibleToolTipPopover"
                :xPosition="xPosition"
                :yPosition="yPosition"
                >
                <template #title>
                    {{ toolTipText }}
                </template>
            </ToolTip>
    </tr>
    <Transition
        class="duration-100 ease-in"
        enter-from-class="transform -translate-y-full"
        leave-to-class="transform -translate-y-full"
        key="progress-update-toolbar"
    >
        <tr v-show="visibleCompanyEvents" class="bg-gray-100 text-xs">
             <td colspan="4" class="sticky z-10 left-0 bg-inherit w-full px-12 pt-3 pb-8  border border-r-2 border-gray-300">
            <!-- <td colspan="4" v-if="companyEvents.length > 0" class="sticky z-10 left-0 bg-inherit w-full px-12 pt-3 pb-8  border border-r-2 border-gray-300"> -->
                <div class="flex flex-col items-start space-y-2">
                    <!-- <div class="font-semibold tracking-wide text-gray-800">
                        Calculate CRM Status From Existing Detail:
                    </div> -->
                    <div class="flex w-full items-center justify-between border-b border-gray-300">
                        <div class="px-2 pb-2 flex space-x-2 w-full cursor-pointer" @click="setAutoComputeEvent">
                            <input 
                                type="radio" 
                                class="cursor-pointer"
                                :style="radioStyle" 
                                :value="true"
                                :disabled="doNotReport"
                                v-model="autoComputeEvent"
                            />
                            <div class="flex items-center" :class="{ 'text-gray-500': doNotReport }">Auto<base-icon-popover-hover popoverTitle="*Auto-Compute CRM Status" :popoverBody="'Furthest CRM Detail will be displayed on report.\nCRM Status is calculated based on furthest CRM Detail.'"><sup class="pl-0.5 italic text-xs">*</sup></base-icon-popover-hover></div>
                        </div>
                        <div class="flex space-x-2 pb-1 justify-end items-center w-full cursor-pointer border-gray-300">
                            <!-- <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="null" v-model="reportedEventID"/>
                            <div class="">Do Not Report Any Detail</div> -->
                            <Switch
                                v-model="doNotReport"
                                :class="doNotReport ? 'bg-dark-secondary border border-dark-primary pr-6' : 'bg-gray-300 border border-gray-400 pr-6'"
                                class="relative inline-flex h-4 w-[38px] items-center rounded-full"
                            >
                                <span class="sr-only">Do Not Report Any CRM Details</span>
                                <span
                                    :class="doNotReport ? 'translate-x-[22px] border-dark-primary' : 'translate-x-0.5 border-gray-400'"
                                    class="inline-block h-3 w-3 transform rounded-full bg-white border transition"></span>
                            </Switch>
                            <div class="text-gray-800 text-xs tracking-wider">Do Not Report Any CRM Details</div>
                        </div>
                </div>
                </div>
                <div class="w-full flex justify-start border-b items-center font-semibold  tracking-wider border-gray-300" :class="{ 'text-gray-400': doNotReport, 'text-gray-700': !doNotReport }">
                    <div class="w-full flex justify-start items-center">
                        <div class="w-[25%] py-1 px-[30px]">Detail</div>
                        <div class="w-[15%] py-1">Date</div>
                        <div class="w-[20%] py-1">User</div>
                        <div class="w-[30%] py-1">Notes</div>
                    </div>
                </div>
                <div class="w-full pb-2 border-gray-300">
                    <div 
                        v-for="crmEvent in companyEvents" 
                        :key="crmEvent.id" 
                        @click="setReportedEventID(crmEvent.id)"
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default text-gray-500': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }"
                        :style="getStyleByEvent(crmEvent)"
                    >
                        <div class="w-full flex h-full items-center justify-start">
                            <div class="w-full flex justify-start items-center">
                                <div class="w-[25%] py-1 flex justify-start items-center space-x-2">
                                    <div class="pt-0.5 pl-2">
                                        <input 
                                            type="radio" 
                                            class="cursor-pointer" 
                                            :style="radioStyle" 
                                            :id="crmEvent.id" 
                                            :value="crmEvent.id"
                                            :disabled="doNotReport"
                                            v-model="reportedEventID"
                                        />
                                    </div>
                                    <div class="flex items-center">
                                        {{ crmEvent.action.action_name }}<base-icon-popover-hover class="bg-inherit" v-if="isFurthestDetail(crmEvent)" popoverTitle="*Auto-Compute CRM Status" :popoverBody="'Furthest CRM Detail will be displayed on report.\nCRM Status is calculated based on furthest CRM Detail.'"><sup class="pl-0.5 italic text-xs">*</sup></base-icon-popover-hover>
                                    </div>
                                </div>
                                <div class="w-[15%] py-1">{{ formatDate(crmEvent.resolved_date)  }}</div>
                                <div class="w-[20%] py-1">{{ crmEvent.user ? `${crmEvent.user.first_name} ${crmEvent.user.last_name}` : '--'  }}</div>
                                <div class="w-[30%] py-1 line-clamp-1">{{ crmEvent.action_notes }}</div>
                            </div>
                        </div>
                    </div>
                    <div 
                        v-for="newCRMAction in newCRMActions"
                        :key="newCRMAction.id"
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default text-gray-500': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(newCRMAction.id)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle" :disabled="doNotReport" :value="newCRMAction.id" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">{{ newCRMAction.action.action_name }}<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <!-- <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-2)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle" :disabled="doNotReport" :value="-2" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - Not Contacted<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-5)"
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle"  :disabled="doNotReport"  :value="-5" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - Dead<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-7)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle"  :disabled="doNotReport"  :value="-7" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - Not Now / Keep Warm<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-4)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle"  :disabled="doNotReport"  :value="-4" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - Active - In Dialogue<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-6)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle"  :disabled="doNotReport"  :value="-6" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - Responded / Coverage<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div>
                    <div 
                        class="border-b border-x border-gray-200"
                        :class="{ 'bg-gray-100 cursor-default': doNotReport, 'cursor-pointer even:bg-white odd:bg-gray-50 hover:bg-[#EEF2FF]': !doNotReport }" 
                        @click="setReportedEventID(-3)"
                        
                    >
                        <div class="py-1  w-full flex space-x-2 h-full items-center justify-start">
                            <div class="pl-2 pt-0.5">
                                <input type="radio" class="cursor-pointer" :style="radioStyle" :disabled="doNotReport"  :value="-3" v-model="reportedEventID"/>
                            </div>
                            <div class="flex items-center">Other - No Response<base-icon-popover-hover  class="bg-inherit" popoverTitle="**Compute CRM Status from new CRM Detail" :popoverBody="'Creates new CRM Detail to display on report.\nCRM Status is calculated based on new CRM Detail.'"><sup class="pl-0.5 italic text-xs">**</sup></base-icon-popover-hover></div>
                        </div>
                    </div> -->
            </div>
            <!-- <div>
                <div class="flex space-x-2">
                    <div class="italic">*</div><div class="italic">Furthest CRM Detail will be displayed on report. CRM Status is calculated based on furthest CRM Detail.</div>
                </div>
                    <div class="flex space-x-2">
                        <div class="italic">**</div><div class="italic">Creates new CRM Detail to display on report. CRM Status is calculated based on new CRM Detail.</div>
                    </div>
                </div> -->
                <!-- <div class="flex flex-col items-start space-y-2 py-2">
                    <div class="font-semibold tracking-wide text-gray-800 ">
                        Calculate CRM Status From New Detail:
                    </div>
                    <div class="px-2 pb-2 mb-2 flex space-x-2 w-full cursor-pointer border-gray-300" @click="setReportedEventID(-2)">
                        <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="-2" v-model="reportedEventID"/>
                        <div class="">Other: Not Contacted (Not Contacted)</div>
                    </div>
                    <div class="px-2 pb-2 mb-2 flex space-x-2 w-full cursor-pointer border-gray-300" @click="setReportedEventID(-3)">
                        <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="-3" v-model="reportedEventID"/>
                        <div class="">Other: No Response (No Response)</div>
                    </div>
                    <div class="px-2 pb-2 mb-2 flex space-x-2 w-full cursor-pointer border-gray-300" @click="setReportedEventID(-4)">
                        <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="-4" v-model="reportedEventID"/>
                        <div class="">Other: Active - In Dialogue (Active - In Dialogue)</div>
                    </div>
                    <div class="px-2 pb-2 mb-2 flex space-x-2 w-full cursor-pointer border-b border-gray-300" @click="setReportedEventID(-5)">
                        <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="-5" v-model="reportedEventID"/>
                        <div class="">Other: Dead (Dead)</div>
                    </div>
                </div> -->
                <!-- <div class="flex flex-col items-start space-y-2">
                    <div class="font-semibold tracking-wide text-gray-800 ">
                        Do Not Calculate CRM Status From Detail:
                    </div>
                    <div class="px-2 pb-2 mb-2 flex space-x-2 w-full cursor-pointer border-gray-300" @click="setReportedEventID(null)">
                        <input type="radio" class="cursor-pointer" :style="radioStyle"   :value="null" v-model="reportedEventID"/>
                        <div class="">Do Not Report Any Detail (Not Contacted)</div>
                    </div>
                </div> -->
            </td>
            <!-- <td colspan="4" v-else class="sticky z-10 left-0 bg-inherit w-full p-6 pb-10  border border-r-2 border-gray-300">
                <div class="w-full flex text-gray-700 justify-start font-semibold  tracking-wider px-14 py-1">
                    No CRM Details
                </div>
            </td>   -->
            <td colspan="23" class="border-y border-r  border-gray-300">

            </td>
        </tr>
    </Transition>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watch } from 'vue';
import ChevronUpMiniSVG from '@/components/svg-components/ChevronUpMiniSVG.vue';
import ChevronDownMiniSVG from '@/components/svg-components/ChevronDownMiniSVG.vue';
import QueueListSVG from '../svg-components/QueueListSVG.vue';
import { Switch } from '@headlessui/vue';
import { formatDate } from '@/composables/formatDate';
import InsertSVG from '../svg-components/InsertSVG.vue';
import ToolTip from '@/components/base-components/ToolTip.vue';

const props = defineProps([
    'company', 
    'headers', 
    'selectAll',
    'expandAll',
    'toggleRowClick',
    'changedReportedEvent',
    'condensedView',
    'clearedSelectedResults',
    'selectedResultIds',
    'newCRMActions',
    'overrideCRMDetailsFlag',
    'bulkCRMDetailId'
])

const emit = defineEmits([
    'set-reported-event', 
    'set-expand-all-neutral',
    'open-crm-details',
    'open-company-modal',
    'add-company-to-progress-update',
    'select-row', 
    'deselect-row', 
])

const crmDetailsBoundary = ref(null);
const xPosition = ref(0);
const yPosition = ref(0);

const visibleCompanyEvents = ref(false)
const blockVisibleEvents = ref(false)

const autoComputeEvent = ref(props.company.auto_compute_reported_crm_event ? true : false)
const autoComputedEvent = ref(props.company.computed_crm_event ? props.company.computed_crm_event : null)
const reportedEventID = ref(props.company.auto_compute_reported_crm_event ? -1 : props.company.reported_crm_event ? props.company.reported_crm_event.id : null)
const lastReportedAutoCompute = ref(props.company.auto_compute_reported_crm_event ? true : false)
const lastReportedEventID = ref(props.company.auto_compute_reported_crm_event ? -1 : props.company.reported_crm_event ? props.company.reported_crm_event.id : null)
const doNotReport = ref(props.company.auto_compute_reported_crm_event ? false : props.company.reported_crm_event ? false : true);

const crmDetailOverride = ref(null)

watch(() => doNotReport.value, () => {
    if(doNotReport.value) {
        setReportedEventID(null)
    } else {
        autoComputeEvent.value = lastReportedAutoCompute.value
        if(autoComputeEvent.value) {
            reportedEventID.value = -1
        } else {
            if(lastReportedEventID.value) {
                reportedEventID.value = lastReportedEventID.value
            } else {
                autoComputeEvent.value = true
                reportedEventID.value = -1
            }
        }
    }
})

watch(() => props.overrideCRMDetailsFlag, () => {
    console.log('watch props.overrideCRMDetailsFlag')
    if(props.selectedResultIds.includes(props.company.id)) {
        crmDetailOverride.value = props.bulkCRMDetailId
        setReportedEventID(crmDetailOverride.value)
        deselectRow()
        crmDetailOverride.value = null
    }
})

watch(() => props.company.default_crm_event, () => {
    if(!props.company.auto_compute_reported_crm_event) {
        if(props.company.default_crm_event) {
            reportedEventID.value = props.company.default_crm_event.id
            lastReportedEventID.value = props.company.default_crm_event.id
        } else {
            doNotReport.value = true
        }
    } else {
        reportedEventID.value = -1
        autoComputeEvent.value = true
    }
}, {deep: true})




const isSelected = ref(props.selectAll)

const visibleToolTipPopover = ref(false);
const toolTipText = ref(null);

const reportedEvent = computed(() => {
    if(autoComputeEvent.value) {
        return autoComputedEvent.value
    }else if(reportedEventID.value) {
        let existingEvent = props.company.crm_actions.find((action) => action.id == reportedEventID.value)
        if(!existingEvent) {
            existingEvent = props.newCRMActions.find((action) => action.id == reportedEventID.value)
        }
        return existingEvent
    } 
    return null
})

watch(() => props.toggleRowClick, () => {
    blockVisibleEvents.value = false
})

watch(() => props.selectAll, () => {
    isSelected.value = props.selectAll
    if(props.selectAll) {
        emit('select-row',props.company.id)
    } else if (!props.selectAll) {
        emit('deselect-row', props.company.id)
    }
})

watch(() => props.expandAll, () => {
    if(props.expandAll == 'expanded') {
        visibleCompanyEvents.value = true
    } else if(props.expandAll == 'collapsed') {
        visibleCompanyEvents.value = false
    }
    // if(companyEvents.value.length > 0){
    //     if(props.expandAll == 'expanded') {
    //         visibleCompanyEvents.value = true
    //     } else if(props.expandAll == 'collapsed') {
    //         visibleCompanyEvents.value = false
    //     }
    // }
    // emit('set-expand-all-neutral', 'neutral')   
})

watch(() => props.clearedSelectedResults, () => {
    // console.log("clearedSelectedResults changed")
    isSelected.value = false
})

watch(() => props.company, () => {
    // console.log('CRMTargetListRow: props.company changed')
    if(props.company.computed_crm_event) {
        autoComputedEvent.value = props.company.computed_crm_event
    }
    // if(props.company.default_crm_event) {
    //     reportedEventID.value = props.company.default_crm_event.id
    // }
}, {deep: true})

watch(() => props.company.auto_compute_reported_crm_event, () => {
    if(props.company.auto_compute_reported_crm_event) {
        autoComputeEvent.value = true
    }
})

watch(() => reportedEventID.value, () => {
    if(reportedEventID.value) {
        doNotReport.value = false
    }
    emit('set-reported-event', props.company, reportedEvent.value, autoComputeEvent.value)
})

const rowStyle = computed(() => {
    return visibleCompanyEvents.value ? 'background-color: #E0E7FF;' : ''  // #D1D5DB #E0E7FF
})

const showAddToProgressUpdate = computed(() => {
    if(props.company.computed_crm_status.label != 'No Response' && props.company.computed_crm_status.label != 'Transaction Closed') {
        return true
    }
    return false
})

const orderedCompanyFields = computed(() => {
    let orderedFields = []
    if(props.condensedView) {
        props.headers.slice(0, 5).forEach(header => {
            orderedFields.push({fieldName: header.fieldName, fieldValue: props.company[header.fieldName]})
        });
    } else {
        props.headers.forEach(header => {
            orderedFields.push({fieldName: header.fieldName, fieldValue: props.company[header.fieldName]})
        });
    }

    return orderedFields
})

const radioStyle = computed(() => {
    // if(autoComputeEvent.value) {
    //     return 'accent-color: #6B7280'
    // }
    return 'accent-color: #25274D'
})

const companyEvents = computed(() => {
    if(props.company) {
        // console.log('companyEvents')
        // console.log(props.company.crm_actions.filter((crmEvent) => crmEvent.campaign_id == props.company.campaign.id))
        return props.company.crm_actions.filter((crmEvent) => crmEvent.campaign_id == props.company.campaign.id)
    }
    return []
})

function selectRow() {
    isSelected.value = true
    emit('select-row', props.company.id)
}

function deselectRow() {
    isSelected.value = false
    emit('deselect-row', props.company.id)
}

function toggleSelected() {
    if(isSelected.value) {
        deselectRow()
    } else {
        selectRow()
    }
}

function showToolTipPopover(elementName, text) {
    console.log(elementName)
    if(elementName == 'crmDetails') {
        if(crmDetailsBoundary.value) {
            xPosition.value = crmDetailsBoundary.value.getBoundingClientRect().left + 250
            yPosition.value = crmDetailsBoundary.value.getBoundingClientRect().bottom + 5
        }

    }
    visibleToolTipPopover.value = true;
    toolTipText.value = text;
  }

  function hideToolTipPopover() {
    visibleToolTipPopover.value = false;
    toolTipText.value = null;
  }

function toggleViewCompanyEvents() {
    if(!blockVisibleEvents.value) {
        visibleCompanyEvents.value = !visibleCompanyEvents.value
    }
    // if(companyEvents.value.length > 0 ) {
    //     if(!blockVisibleEvents.value) {
    //         visibleCompanyEvents.value = !visibleCompanyEvents.value
    //     }
    // }
}

function openCRMDetails() {
    // blockVisibleEvents.value = true
    emit('open-crm-details', props.company)
}

function openCompanyModal() {
    emit('open-company-modal', props.company)
}

function setReportedEventID(eventID) {
    if(!doNotReport.value || !eventID) {
        reportedEventID.value = eventID
        autoComputeEvent.value = false
    } else {
        reportedEventID.value = null
    }
    // console.log('set Reported Event', eventID)

}

function addCompanyToProgressUpdate() {
    // console.log('CRMTargetListRow: addCompanyToProgressUpdate')
    // blockVisibleEvents.value = true
    emit('add-company-to-progress-update', props.company.id)
}

function setAutoComputeEvent() {
    // reportedEventID.value = autoComputeEvent.value.id
    if(!doNotReport.value) {
        reportedEventID.value = -1
        autoComputeEvent.value = true
    }
}

function isFurthestDetail(crmEvent) {
    if(autoComputedEvent.value && crmEvent && crmEvent.id == autoComputedEvent.value.id) {
        return true
    }
    return false
}

function handleFieldDisplay(field) {
    if(field.fieldValue) {
        if(field.fieldName == 'id') {
            return null;
        }
        if(field.fieldName == 'campaign') {
            return field.fieldValue.campaign_name
        }
        else if(field.fieldName == 'tier_object') {
            return field.fieldValue.tier_name
        }
        else if(field.fieldName == 'crm_actions') {
            if(field.fieldValue.length == 1) {
                return `${field.fieldValue.length} CRM Action`
            }
            return `${field.fieldValue.length} CRM Actions`
        }   
        else if(field.fieldName == 'reported_crm_event') {
            // console.log(reportedEvent.value)
            let eventString = '';
            if(props.company.auto_compute_reported_crm_event) {
                 eventString += `*`
            }
            if(reportedEvent.value) {
                if(reportedEvent.value.resolved_date) {
                    eventString += `${formatDate(reportedEvent.value.resolved_date)}: `
                }
                if(reportedEvent.value.action.action_name) {
                    eventString += `${reportedEvent.value.action.action_name}`
                }
                if(eventString.length == 0) {
                    eventString += '--'
                }
            } else {
                eventString += '--'
            }
            return eventString
        }
        else if (field.fieldName == 'computed_crm_status') {
            return field.fieldValue.label
        }
        return field.fieldValue
    } else if(field.fieldName == 'reported_crm_event') {
        let eventString = '';
            if(reportedEvent.value) {
                if(reportedEvent.value.resolved_date) {
                    eventString += `${formatDate(reportedEvent.value.resolved_date)}: `
                }
                if(reportedEvent.value.action.action_name) {
                    eventString += `${reportedEvent.value.action.action_name}`
                }
                if(eventString.length == 0) {
                    eventString += '--'
                }
            } else {
                if(props.company.auto_compute_reported_crm_event) {
                     eventString += '*No Events'
                } else {
                    eventString += 'Do Not Use Event'
                }
            }
            return eventString
    }
    return ''
}


// 3 cols
function getCellStyleByIndex(index) {
    if(props.condensedView) {
        if(index == 0) {
            return `position: sticky; max-width: 52px; width: 52px; min-width: 52px; left: 0px; z-index: 10;`
        } else if(index == 1) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 52px; z-index: 10;` // 300
        } else if(index == 2) {
            return `position: sticky; max-width: 300px; width: 300px; min-width: 300px; left: 252px; z-index: 10;` // 150
        } else if (index == 3) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 552px; z-index: 10; border-right: 2px solid #D1D5DB;`
        } else if (index == 4) {
            return `position: sticky; max-width: 30px; width: 30px; min-width: 30px; left: 752px; z-index: 10;`
        } else {
            return 'z-index: 10'
        }
    } else {
        if(index == 0) {
            return `position: sticky; max-width: 52px; width: 52px; min-width: 52px; left: 0px; z-index: 10;`
        } else if(index == 1) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 52px; z-index: 10;`
        } else if(index == 2) {
            return `position: sticky; max-width: 300px; width: 300px; min-width: 300px; left: 252px; z-index: 10;`
        } else if (index == 3) {
            return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 552px; z-index: 10; border-right: 2px solid #D1D5DB;`
        } else {
            return 'z-index: 5'
        }
    }

}


// 4 cols
// function getCellStyleByIndex(index) {
//     if(index == 0) {
//         return `position: sticky; max-width: 46px; width: 46px; min-width: 46px; left: 0px; z-index: 10;`
//     } else if(index == 1) {
//         return `position: sticky; max-width: 200px; width: 200px; min-width: 200px; left: 46px; z-index: 10;`
//     } else if(index == 2) {
//         return `position: sticky; max-width: 300px; width: 300px; min-width: 300px; left: 246px; z-index: 10;`
//     } else if (index == 3) {
//         return `position: sticky; max-width: 150px; width: 150px; min-width: 150px; left: 546px; z-index: 10;`
//     } else if (index == 4) {
//         return `position: sticky; max-width: 280px; width: 280px; min-width: 280px; left: 696px; z-index: 10; border-right: 2px solid #D1D5DB;`
//     } else {
//         return 'z-index: 5'
//     }
// }

function getStyleByEvent(crmEvent) {
    if(autoComputeEvent.value) {
        if(reportedEvent.value && crmEvent && crmEvent.id == reportedEvent.value.id) {
            return 'background-color: #e0e7ff;' 
        } else {
            return '' 
        }
    } else {
        if(reportedEvent.value && crmEvent && crmEvent.id == reportedEvent.value.id) {
            return 'background-color: #C7D2FE;'
        }
        return ''
    }
}

// function getAutoComputeFlagByEvent(crmEvent) {
//     if(crmEvent && crmEvent.id == autoComputedEvent.value.id) {
//         return 'background-color: #FDE68A;'
//     } else if(reportedEvent.value && crmEvent && crmEvent.id == reportedEvent.value.id) {
//         return 'background-color: #A5B4FC'
//     }
//     return 'background-color: inherit'
// }


// function isFurthestAction(crmAction) {
//     if(crmAction && props.company.computed_crm_event) {
//         if(crmAction.id == props.company.computed_crm_event.id) {
//             return true
//         } 
//     }
//     return false
// }



</script>